import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import { backgroundProcessApi } from '../services/api';
import { BackgroundProcess } from '../types';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import { appDateValue, formatAppDateTime } from '../utils/dateFormat';

const ProcessHistory: React.FC = () => {
  const [backgroundProcesses, setBackgroundProcesses] = useState<BackgroundProcess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof BackgroundProcess>('startedTs');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterProcess, setFilterProcess] = useState<string>('');

  useEffect(() => {
    loadBackgroundProcesses();
  }, []);

  const getRowBgColor = (process: BackgroundProcess) => {
    const status = process.statusCd;
    if (status === 'FAILED') return (theme: Theme) => alpha(theme.palette.error.light, 0.3);
    if (status === 'COMPLETED') return (theme: Theme) => alpha(theme.palette.success.light, 0.3);
    if (status === 'SKIP') return (theme: Theme) => alpha(theme.palette.grey[300], 0.5);
    return undefined;
  };

  const loadBackgroundProcesses = async () => {
    try {
      setLoading(true);
      const response = await backgroundProcessApi.getLatest();
      setBackgroundProcesses(response);
      setError(null);
    } catch (err) {
      setError('Ошибка загрузки данных');
      setBackgroundProcesses([]);
      console.error('Error loading background processes:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'default';
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'running':
        return 'info';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDateTime = (dateTime: string) =>
    formatAppDateTime(dateTime, true, '-', true);

  const getProcessDisplayName = (processCode: string) => {
    switch (processCode) {
      case 'COLLECT_ALL_VIDEO':
        return 'Сбор видео (все платформы)';
      case 'COLLECT_VIDEO_VK':
        return 'Сбор видео VK Video';
      case 'COLLECT_VIDEO_YOUTUBE':
        return 'Сбор видео YouTube';
      case 'COLLECT_VIDEO_RUTUBE':
        return 'Сбор видео Rutube';
      case 'COLLECT_VIEWS':
        return 'Сбор просмотров (все платформы)';
      case 'COLLECT_VIEWS_VK_VIDEO':
        return 'Сбор просмотров VK Video';
      case 'COLLECT_VIEWS_YOUTUBE':
        return 'Сбор просмотров YouTube';
      case 'COLLECT_VIEWS_RUTUBE':
        return 'Сбор просмотров Rutube';
      case 'VKORD_SEND':
        return 'Отправка статистики в VkOrd';
      default:
        return processCode;
    }
  };

  const handleSort = (property: keyof BackgroundProcess) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: any, newPage: number) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const processOptions = Array.from(new Set(backgroundProcesses.map(p => getProcessDisplayName(p.processCd))));
  const statusOptions = Array.from(new Set(backgroundProcesses.map(p => p.statusCd)));

  const filtered = backgroundProcesses.filter(p =>
    (!filterStatus || (p.statusCd ? p.statusCd.toLowerCase().includes(filterStatus.toLowerCase()) : false)) &&
    (!filterProcess || (getProcessDisplayName(p.processCd) ? getProcessDisplayName(p.processCd).toLowerCase().includes(filterProcess.toLowerCase()) : false))
  );
  const sorted = filtered.sort((a, b) => {
    let aValue = a[orderBy];
    let bValue = b[orderBy];
    if (orderBy === 'startedTs' || orderBy === 'endedTs') {
      const aNum = aValue ? appDateValue(aValue as string) : 0;
      const bNum = bValue ? appDateValue(bValue as string) : 0;
      if (aNum < bNum) return order === 'asc' ? -1 : 1;
      if (aNum > bNum) return order === 'asc' ? 1 : -1;
      return 0;
    } else {
      const aStr = aValue ? String(aValue) : '';
      const bStr = bValue ? String(bValue) : '';
      if (aStr < bStr) return order === 'asc' ? -1 : 1;
      if (aStr > bStr) return order === 'asc' ? 1 : -1;
      return 0;
    }
  });
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1.5 }}>
        История запусков
      </Typography>
      <Card>
        <CardContent>
          <Box display="flex" gap={2} mb={2}>
            <Select
              displayEmpty
              value={filterProcess}
              onChange={e => setFilterProcess(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              renderValue={selected => selected || 'Фильтр по процессу'}
            >
              <MenuItem value="">Все процессы</MenuItem>
              {processOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
            <Select
              displayEmpty
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              size="small"
              sx={{ minWidth: 200 }}
              renderValue={selected => selected || 'Фильтр по статусу'}
            >
              <MenuItem value="">Все статусы</MenuItem>
              {statusOptions.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Процесс</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'startedTs'}
                      direction={orderBy === 'startedTs' ? order : 'asc'}
                      onClick={() => handleSort('startedTs')}
                    >
                      Начало
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'endedTs'}
                      direction={orderBy === 'endedTs' ? order : 'asc'}
                      onClick={() => handleSort('endedTs')}
                    >
                      Завершение
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Сообщение</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.map((p) => (
                  <TableRow key={p.backgroundProcessId} sx={{ backgroundColor: getRowBgColor(p) }}>
                    <TableCell>{getProcessDisplayName(p.processCd)}</TableCell>
                    <TableCell>
                      <Chip label={p.statusCd} color={getStatusColor(p.statusCd)} size="small" />
                    </TableCell>
                    <TableCell>{p.startedTs ? formatDateTime(p.startedTs) : '-'}</TableCell>
                    <TableCell>{p.endedTs ? formatDateTime(p.endedTs) : '-'}</TableCell>
                    <TableCell>{p.message || '-'}</TableCell>
                    <TableCell>
                      {p.statusCd === 'COMPLETED' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            window.location.href = `${process.env.PUBLIC_URL || '/ord'}/data-view?processId=${p.backgroundProcessId}`;
                          }}
                        >
                          Детали
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sorted.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProcessHistory; 
